import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { ApiError } from "../api/client"
import { applyForMicroLoan, getMicroLoanEligibility } from "../api/endpoints/portal"
import { Skeleton } from "../components/skeleton"
import { formatCurrency, formatDate } from "../utils/formatting"

function reasonLabel(code: string) {
  switch (code) {
    case "MICRO_LOAN_DISABLED":
    case "MICRO_LOAN_PORTAL_DISABLED":
      return "Quick loans are currently unavailable."
    case "CLIENT_INACTIVE":
      return "Your account is inactive."
    case "KYC_NOT_APPROVED":
      return "Your KYC review must be approved first."
    case "PHONE_NOT_VERIFIED":
      return "Verify your phone number before requesting a quick loan."
    case "NATIONAL_ID_REQUIRED":
      return "Complete your national ID details first."
    case "CLIENT_TOO_YOUNG":
      return "You do not meet the minimum age requirement."
    case "MICRO_LOAN_TIER_FROZEN":
      return "Your quick-loan tier is temporarily frozen."
    case "STANDARD_LOAN_OVERDUE":
      return "Resolve your overdue standard loan first."
    case "ACTIVE_MICRO_LOAN_EXISTS":
      return "You already have an active quick loan."
    case "EMPLOYMENT_VERIFICATION_REQUIRED":
      return "A verified proof-of-income document is required."
    default:
      return code.replaceAll("_", " ")
  }
}

function addDays(date: Date, days: number) {
  const value = new Date(date)
  value.setDate(value.getDate() + days)
  return value
}

function toDateInputValue(date: Date) {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000)
  return local.toISOString().slice(0, 10)
}

function fromDateInputValue(value: string) {
  return new Date(`${value}T12:00:00`)
}

function differenceInDays(start: Date, end: Date) {
  const startValue = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime()
  const endValue = new Date(end.getFullYear(), end.getMonth(), end.getDate()).getTime()
  return Math.round((endValue - startValue) / 86_400_000)
}

function buildPresetAmounts(maxAmount: number) {
  const presets = [0.25, 0.5, 0.75, 1]
    .map((factor) => Math.max(1, Math.round(maxAmount * factor * 100) / 100))
    .filter((value, index, values) => value <= maxAmount && values.indexOf(value) === index)

  return presets
}

function isQuickLoanUnavailable(reasons: string[]) {
  return (
    reasons.includes("MICRO_LOAN_DISABLED") || reasons.includes("MICRO_LOAN_PORTAL_DISABLED")
  )
}

export function QuickLoanPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const eligibilityQuery = useQuery({
    queryKey: ["portal-micro-loan-eligibility"],
    queryFn: () => getMicroLoanEligibility().then((response) => response.data),
  })
  const [amount, setAmount] = useState("")
  const [dueDate, setDueDate] = useState("")
  const [disbursementMethod, setDisbursementMethod] = useState<"ECOCASH" | "INNBUCKS">("ECOCASH")
  const [disbursementAccount, setDisbursementAccount] = useState("")
  const [acceptedDisclosure, setAcceptedDisclosure] = useState(false)
  const [feedback, setFeedback] = useState("")
  const [error, setError] = useState("")

  const eligibility = eligibilityQuery.data
  const tierTerms = eligibility?.tier.terms ?? null
  const today = useMemo(() => new Date(), [])

  useEffect(() => {
    if (!tierTerms) {
      return
    }

    setDueDate((current) =>
      current || toDateInputValue(addDays(new Date(), tierTerms.defaultTenorDays)),
    )
  }, [tierTerms])

  const minDueDate = tierTerms ? toDateInputValue(addDays(today, tierTerms.minTenorDays)) : ""
  const maxDueDate = tierTerms ? toDateInputValue(addDays(today, tierTerms.maxTenorDays)) : ""
  const maxAmount = tierTerms ? tierTerms.principalCeiling / 100 : 0
  const presetAmounts = useMemo(() => buildPresetAmounts(maxAmount), [maxAmount])
  const principalAmount = useMemo(() => Math.round(Number(amount || "0") * 100), [amount])
  const selectedTenorDays = useMemo(() => {
    if (!dueDate) {
      return null
    }

    return differenceInDays(today, fromDateInputValue(dueDate))
  }, [dueDate, today])
  const amountError =
    !amount.trim()
      ? "Enter the amount you want to request."
      : !Number.isFinite(principalAmount) || principalAmount <= 0
        ? "Enter a valid quick-loan amount."
        : principalAmount > Math.round(maxAmount * 100)
          ? `Your current tier ceiling is ${eligibility?.tier.terms.principalCeilingDisplay}.`
          : ""
  const dueDateError =
    !dueDate
      ? "Choose a due date."
      : selectedTenorDays === null ||
          !tierTerms ||
          selectedTenorDays < tierTerms.minTenorDays ||
          selectedTenorDays > tierTerms.maxTenorDays
        ? `Choose a due date between ${formatDate(minDueDate)} and ${formatDate(maxDueDate)}.`
        : ""
  const accountError = !disbursementAccount.trim() ? "Enter the wallet number for disbursement." : ""

  const pricingPreview = useMemo(() => {
    if (
      !eligibility ||
      !Number.isFinite(principalAmount) ||
      principalAmount <= 0 ||
      !selectedTenorDays
    ) {
      return null
    }

    const fee = Math.round(principalAmount * eligibility.tier.terms.applicationFeeRate)
    const interestAmount = Math.round(principalAmount * eligibility.tier.terms.interestRate)
    const totalRepayable = principalAmount + interestAmount
    return {
      principalAmount,
      applicationFeeAmount: fee,
      netDisbursedAmount: Math.max(0, principalAmount - fee),
      interestAmount,
      totalRepayableAmount: totalRepayable,
      dueDate,
      effectiveCostPercent:
        principalAmount > 0 ? ((fee + interestAmount) / principalAmount) * 100 : 0,
    }
  }, [amount, dueDate, eligibility, principalAmount, selectedTenorDays])

  const applyMutation = useMutation({
    mutationFn: async () => {
      const resolvedTenor = selectedTenorDays ?? eligibility?.tier.terms.defaultTenorDays ?? 0

      if (amountError || dueDateError || accountError) {
        throw new Error(amountError || dueDateError || accountError)
      }

      return applyForMicroLoan({
        principalAmount,
        tenorDays: resolvedTenor,
        disbursementMethod,
        disbursementAccount,
        portalDisclosureAccepted: true,
        portalDisclosureAcceptedAt: new Date().toISOString(),
      })
    },
    onSuccess: async (response) => {
      setFeedback(
        response.data.manualReviewRequired
          ? "Quick loan submitted for manual review."
          : "Quick loan submitted and queued for disbursement review.",
      )
      setError("")
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["portal-dashboard"] }),
        queryClient.invalidateQueries({ queryKey: ["portal-loan"] }),
        queryClient.invalidateQueries({ queryKey: ["portal-micro-loan-eligibility"] }),
      ])
      navigate(`/loans/${response.data.loan.loanId}`)
    },
    onError: (mutationError) => {
      if (mutationError instanceof ApiError && Array.isArray(mutationError.details)) {
        setError(String(mutationError.message))
      } else {
        setError((mutationError as Error).message)
      }
      setFeedback("")
    },
  })

  const submitDisabled =
    applyMutation.isPending ||
    Boolean(amountError) ||
    Boolean(dueDateError) ||
    Boolean(accountError) ||
    !acceptedDisclosure

  if (eligibilityQuery.isLoading) {
    return (
      <div className="page-stack">
        <Skeleton height={140} />
        <Skeleton height={240} />
      </div>
    )
  }

  if (!eligibility) {
    return null
  }

  const resolvedTierTerms = eligibility.tier.terms

  if (isQuickLoanUnavailable(eligibility.reasons)) {
    return (
      <div className="page-stack">
        <section className="hero-card">
          <div>
            <p className="eyebrow">Quick loan</p>
            <h2>Quick loans are unavailable</h2>
            <p className="subtle">
              Quick-loan requests are currently disabled for the portal. Return to the dashboard to review the
              borrowing options available to your account.
            </p>
          </div>
        </section>
        <section className="card">
          <div className="button-row">
            <Link className="primary-button" to="/">
              Back to dashboard
            </Link>
          </div>
        </section>
      </div>
    )
  }

  return (
    <div className="page-stack">
      <section className="hero-card">
        <div>
          <p className="eyebrow">Quick loan</p>
          <h2>{eligibility.tier.tierName}</h2>
          <p className="subtle">
            Borrow up to {eligibility.tier.terms.principalCeilingDisplay} for{" "}
            {resolvedTierTerms.minTenorDays}-{resolvedTierTerms.maxTenorDays} days.
          </p>
        </div>
        <div className="hero-metrics">
          <div>
            <strong>{eligibility.tier.currentTier}</strong>
            <span>Current tier</span>
          </div>
          <div>
            <strong>{eligibility.tier.tierScore}</strong>
            <span>On-time cycles</span>
          </div>
        </div>
      </section>

      {eligibility.tier.nextTier ? (
        <section className="card">
          <div className="section-header">
            <div>
              <p className="eyebrow">Tier progress</p>
              <h3>Path to {eligibility.tier.nextTier.tierName}</h3>
            </div>
          </div>
          <p className="subtle">
            {eligibility.tier.nextTier.remainingCycles} more on-time closed loans to unlock{" "}
            {eligibility.tier.nextTier.principalCeilingDisplay}.
          </p>
        </section>
      ) : null}

      {!eligibility.eligible ? (
        <section className="card">
          <div className="status-banner error">
            <strong>You are not currently eligible for a quick loan.</strong>
            <ul style={{ marginTop: 12, paddingLeft: 20 }}>
              {eligibility.reasons.map((reason) => (
                <li key={reason}>{reasonLabel(reason)}</li>
              ))}
            </ul>
            {eligibility.activeMicroLoan ? (
              <div className="hint-copy" style={{ marginTop: 12 }}>
                Active quick loan: {eligibility.activeMicroLoan.loanId} ·{" "}
                {formatCurrency("USD", eligibility.activeMicroLoan.principalAmount)}
              </div>
            ) : null}
            <div className="button-row" style={{ marginTop: 16 }}>
              <Link className="ghost-button" to="/">
                Back to dashboard
              </Link>
              <Link className="ghost-button" to="/onboarding">
                Review profile and KYC
              </Link>
            </div>
          </div>
        </section>
      ) : (
        <section className="card">
          <div className="section-header">
            <div>
              <p className="eyebrow">Application</p>
              <h3>Request a quick loan</h3>
            </div>
          </div>
          {feedback ? <div className="status-banner success">{feedback}</div> : null}
          {error ? <div className="status-banner error">{error}</div> : null}

          <form
            className="form-grid"
            onSubmit={(event) => {
              event.preventDefault()
              applyMutation.mutate()
            }}
          >
            <label>
              Amount (USD)
              <input
                inputMode="decimal"
                max={maxAmount}
                min={0}
                onChange={(event) => setAmount(event.target.value)}
                placeholder={`Max ${maxAmount.toFixed(2)}`}
                step="0.01"
                value={amount}
              />
            </label>
            <div className="hint-copy">
              Tier ceiling: {eligibility.tier.terms.principalCeilingDisplay}
            </div>
            <div className="button-row" style={{ gridColumn: "1 / -1" }}>
              {presetAmounts.map((preset) => (
                <button
                  className="ghost-button"
                  key={preset}
                  onClick={() => setAmount(preset.toFixed(2))}
                  type="button"
                >
                  {preset.toFixed(2)}
                </button>
              ))}
            </div>
            {amountError ? <div className="status-banner error">{amountError}</div> : null}

            <label>
              Due date
              <input
                max={maxDueDate}
                min={minDueDate}
                onChange={(event) => setDueDate(event.target.value)}
                type="date"
                value={dueDate}
              />
            </label>
            <div className="hint-copy">
              Allowed due dates: {formatDate(minDueDate)} to {formatDate(maxDueDate)}
            </div>
            {dueDateError ? <div className="status-banner error">{dueDateError}</div> : null}

            <label>
              Disbursement method
              <select
                onChange={(event) =>
                  setDisbursementMethod(event.target.value as "ECOCASH" | "INNBUCKS")
                }
                value={disbursementMethod}
              >
                <option value="ECOCASH">EcoCash</option>
                <option value="INNBUCKS">InnBucks</option>
              </select>
            </label>

            <label>
              Wallet number
              <input
                onChange={(event) => setDisbursementAccount(event.target.value)}
                placeholder="+2637..."
                value={disbursementAccount}
              />
            </label>
            {accountError ? <div className="status-banner error">{accountError}</div> : null}

            {pricingPreview ? (
              <div className="card" style={{ gridColumn: "1 / -1" }}>
                <div className="section-header">
                  <div>
                    <p className="eyebrow">Cost summary</p>
                    <h3>What you will receive and repay</h3>
                  </div>
                </div>
                <div className="list-row">
                  <strong>Net received</strong>
                  <span>{formatCurrency("USD", pricingPreview.netDisbursedAmount)}</span>
                </div>
                <div className="list-row">
                  <strong>Requested amount</strong>
                  <span>{formatCurrency("USD", pricingPreview.principalAmount)}</span>
                </div>
                <div className="list-row">
                  <strong>Application fee</strong>
                  <span>{formatCurrency("USD", pricingPreview.applicationFeeAmount)}</span>
                </div>
                <div className="list-row">
                  <strong>Interest amount</strong>
                  <span>{formatCurrency("USD", pricingPreview.interestAmount)}</span>
                </div>
                <div className="list-row">
                  <strong>Total repayable</strong>
                  <span>{formatCurrency("USD", pricingPreview.totalRepayableAmount)}</span>
                </div>
                <div className="list-row">
                  <strong>Due date</strong>
                  <span>{formatDate(pricingPreview.dueDate)}</span>
                </div>
                <div className="list-row">
                  <strong>Effective cost</strong>
                  <span>{pricingPreview.effectiveCostPercent.toFixed(2)}%</span>
                </div>
                <p className="hint-copy" style={{ marginTop: 12 }}>
                  Quick loans are repaid as a single bullet payment on the due date.
                </p>
              </div>
            ) : null}

            <label
              className="subtle"
              style={{ display: "flex", gap: 12, alignItems: "flex-start", gridColumn: "1 / -1" }}
            >
              <input
                checked={acceptedDisclosure}
                onChange={(event) => setAcceptedDisclosure(event.target.checked)}
                type="checkbox"
              />
              <span>
                {pricingPreview
                  ? `I understand that I will receive ${formatCurrency("USD", pricingPreview.netDisbursedAmount)} after fees and must repay ${formatCurrency("USD", pricingPreview.totalRepayableAmount)} by ${formatDate(pricingPreview.dueDate)} in one payment.`
                  : "I understand that the application fee is deducted before disbursement and I must repay the total repayable amount by the selected due date."}
              </span>
            </label>

            <div className="button-row" style={{ gridColumn: "1 / -1" }}>
              <button className="primary-button" disabled={submitDisabled} type="submit">
                {applyMutation.isPending ? "Submitting..." : "Request quick loan"}
              </button>
            </div>
          </form>
        </section>
      )}
    </div>
  )
}
